import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { Target, Heart, Users, Award, Cpu, ArrowRight, CheckCircle2 } from 'lucide-react';
import { siteImages } from '../data/mock';

const OurMissionPage = () => {
  useEffect(() => {
    document.title = 'Our Mission | EC Tech Savvy';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main className="">
        {/* Banner Section */}
        <section className="relative h-64 md:h-80 overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src={siteImages.heroLifestyle}
              alt="Our Mission"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/40" />
          </div>
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-50 border border-indigo-200 rounded-full text-indigo-600 text-xs font-semibold mb-4">
                <Target className="w-3.5 h-3.5" />
                OUR PURPOSE
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-gray-900 font-extrabold mb-3">
                Our Mission
              </h1>
              <p className="text-gray-600 max-w-lg">
                Empowering tech lovers to achieve their best setup
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Mission Statement */}
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8 md:p-12 mb-16 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-athletic-500 to-athletic-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Cpu className="w-10 h-10 text-gray-900" />
            </div>
            <h2 className="font-display text-3xl lg:text-4xl text-gray-900 font-extrabold mb-6">
              Power Up Your Setup. Live the Tech Lifestyle.
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
              At EC Tech Savvy, our mission is simple: to provide our customers with the premium electronics and smart gadgets they need to power up their lives. We believe that the right equipment can transform your daily workflow, and we're dedicated to curating only the best products for our customers.
            </p>
          </div>

          {/* Core Values */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-indigo-50 text-indigo-600 text-xs font-bold uppercase tracking-wider rounded-full mb-4 border border-indigo-100">
                Core Values
              </span>
              <h2 className="font-display text-3xl lg:text-4xl text-gray-900 font-extrabold">
                What We Stand For
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Target, title: 'Excellence', desc: 'We strive for excellence in every product we offer and every interaction with our customers.' },
                { icon: Heart, title: 'Passion', desc: 'We\'re passionate about technology and helping users reach their full potential.' },
                { icon: Users, title: 'Community', desc: 'We believe in building a community of customers who support and inspire each other.' },
                { icon: Award, title: 'Quality', desc: 'We never compromise on quality. Every product meets our high standards.' },
              ].map((value, i) => (
                <div key={i} className="bg-gray-50 border border-gray-200 rounded-2xl p-6 hover:border-athletic-500/50 transition-all group">
                  <div className="w-14 h-14 bg-indigo-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-athletic-500/20 transition-colors">
                    <value.icon className="w-7 h-7 text-indigo-600" />
                  </div>
                  <h3 className="font-display font-bold text-gray-900 text-lg mb-2">{value.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Our Promise */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="relative">
              <div className="aspect-video rounded-2xl overflow-hidden border border-gray-200">
                <img 
                  src={siteImages.powerBackground}
                  alt="Our Promise"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div>
              <span className="inline-block px-4 py-1.5 bg-indigo-50 text-indigo-600 text-xs font-bold uppercase tracking-wider rounded-full mb-4 border border-indigo-100">
                Our Promise
              </span>
              <h2 className="font-display text-3xl text-gray-900 font-extrabold mb-6">
                We're Here for Your Journey
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Whether you're just starting your electronics journey or you're a seasoned tech enthusiast, we're here to support you. Our team is dedicated to helping you find the right tech for your goals.
              </p>
              <div className="space-y-4">
                {[
                  'Premium quality electronics and tech gear',
                  'Fast shipping across the USA',
                  '30-day hassle-free returns',
                  'Dedicated customer support',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                    <span className="text-gray-900">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center bg-gradient-to-br from-athletic-500/10 to-athletic-600/10 border border-indigo-200 rounded-2xl p-12">
            <h2 className="font-display text-3xl font-extrabold text-gray-900 mb-4">
              Join the EC Tech Savvy Family
            </h2>
            <p className="text-gray-600 mb-8 max-w-xl mx-auto">
              Start your journey with premium electronics and smart gadgets designed for performance.
            </p>
            <Link 
              to="/shop"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-athletic-500 to-athletic-600 text-gray-900 font-bold uppercase tracking-wider rounded-xl hover:from-athletic-400 hover:to-athletic-500 transition-all shadow-lg"
            >
              Shop Now
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default OurMissionPage;
